import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { SeaDayReportResponse, WorkerRequest, WorkerResponse, WorkersClient, ReportsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface Worker extends WorkerResponse { }

export interface SeaDayReport extends SeaDayReportResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  workerList: Worker[];
  selectedWorker?: Worker;
}

const initialState: State = {
  isLoading: false,
  error: false,
  workerList: [],
  selectedWorker: undefined
};

const workerSlice = createSlice({
  name: 'worker',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getWorkerListSuccess: (state, action) => {
      state.workerList = action.payload;
      state.isLoading = false;
    },
    getWorkerSuccess: (state, action: PayloadAction<Worker | undefined>) => {
      state.selectedWorker = action.payload;
      state.isLoading = false;
    },
    addNewWorkerSuccess: (state, action: PayloadAction<Worker>) => {
      const newWorker = action.payload;
      state.workerList = [newWorker, ...state.workerList];
      state.isLoading = false;
    },
    updateWorkerSuccess: (state, action: PayloadAction<Worker>) => {
      const updatedItem = action.payload;
      const index = state.workerList.findIndex(item => item.workerId === updatedItem.workerId);
      state.workerList = [...state.workerList.slice(0, index), updatedItem, ...state.workerList.slice(index + 1)];
      state.isLoading = false;
    },
  },
});

// Reducer
export default workerSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
  getWorkerListSuccess,
  getWorkerSuccess,
  addNewWorkerSuccess,
  updateWorkerSuccess,
} = workerSlice.actions;

// for useSelector
export const selectWorker = (state: RootState) => state.worker as State;

// ----------------------------------------------------------------------
export function getAllWorkers(ignoreCache: boolean = false) {
  return async (dispatch, getState) => {
    try {
      const { workerList } = selectWorker(getState());
      if (ignoreCache || workerList.length === 0) {
        dispatch(workerSlice.actions.startLoading());
        const client = new WorkersClient(undefined, axios);
        const response = await client.getAll(false);
        dispatch(workerSlice.actions.getWorkerListSuccess(response));
      }
    } catch (error) {
      dispatch(workerSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function getWorker(id: number) {
  return async (dispatch, getState) => {
    if (id > 0) {
      dispatch(workerSlice.actions.startLoading());
      try {
        const client = new WorkersClient(undefined, axios);
        const response = await client.get(id);
        dispatch(workerSlice.actions.getWorkerSuccess(response));
      } catch (error) {
        dispatch(workerSlice.actions.hasError(true));
        handleError(error);
      }
    } else {
      dispatch(workerSlice.actions.getWorkerSuccess(undefined));
    }
  };
}

export function getSeaDayReport(externalId: string) {
  return async (dispatch) => {
    try {
      const client = new ReportsClient(undefined, axios);
      return await client.getSeaDayReport(externalId);
    } catch (error) {
      dispatch(workerSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createWorker(worker: Worker, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(workerSlice.actions.startLoading());
    try {
      const { workerId, ...payload } = worker;
      const request = payload as WorkerRequest;
      const client = new WorkersClient(undefined, axios);
      const response = await client.create(request);
      dispatch(workerSlice.actions.addNewWorkerSuccess(response));
      if (showNotification) {
        toast.success(`${response.firstName} ${response.lastName} created successfully!`);
      }
    } catch (error) {
      dispatch(workerSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function updateWorker(worker: Worker, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(workerSlice.actions.startLoading());
    try {
      const { workerId, ...payload } = worker;
      const request = payload as WorkerRequest;
      const client = new WorkersClient(undefined, axios);
      const response = await client.edit(workerId, request);
      dispatch(workerSlice.actions.updateWorkerSuccess(response));
      if (showNotification) {
        toast.success(`${response.firstName} ${response.lastName} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
      }
    } catch (error) {
      dispatch(workerSlice.actions.hasError(true));
      handleError(error);
    }
  };
}